import type { RefObject } from 'react';
import type { CompanionDto } from '../api/companions';
import { lowCareCue } from './CareMeters';
import { HabitatDockIcon, type HabitatDockIconId } from './dockIcons';
import { formatEta } from '../lib/formatEta';

// `RefObject<HTMLButtonElement>` (not `| null`): that is what @types/react 18's `<button ref>` accepts, and
// exactly what the screen's `useRef<HTMLButtonElement>(null)` triggers are.
type ButtonRef = RefObject<HTMLButtonElement>;

export type DashboardProps = {
  companion: CompanionDto; now?: Date; disabled: boolean;
  /** #14.1 (CLAUDE.md "Hide the empty"): Dress and Looks render only while the `wardrobe` flag is
   *  on — the wardrobe is parked until its art exists. `HabitatScreen` reads `useFlag('wardrobe')`
   *  and passes it down; nothing here re-derives it. */
  wardrobeOn: boolean;
  /** The SCREEN's own cooldown verdict (`playReadyAt != null`) — never re-derived here from the
   *  wall clock, so the Dashboard and the phone dock can never disagree about the same state. */
  onCooldown: boolean;
  /** Transient Play blockers the phone dock also honours: a care action in flight, a playlet
   *  already running. Natively disabled (not informational, unlike the cooldown). */
  playBlocked?: boolean;
  onFeed: () => void; onPlay: () => void; onGroom: () => void;
  onDress: () => void; onEquip: () => void; onLooks: () => void; onAbilities: () => void;
  /** The screen's sheet trigger refs. On the desk the dock — where these refs normally live —
   *  is not rendered, so each sheet's `restoreFocusRef` must point at the Dashboard's button
   *  instead, or a closing sheet drops keyboard focus on `<body>` (fix round 1, finding 1). */
  feedRef?: ButtonRef; playRef?: ButtonRef; groomRef?: ButtonRef;
  dressRef?: ButtonRef; equipRef?: ButtonRef; looksRef?: ButtonRef; abilitiesRef?: ButtonRef;
  /** e.g. "1 of 6" — passed by the screen from data it already has, or omitted. */
  looksCount?: number; abilitiesOpen?: string;
};

/**
 * THE DASHBOARD (spec D2, §4.5 Habitat) — the desk's right panel. Three stat cards, each a meter with
 * its own action, then the four other dock actions, each with the state it already has. Numbers are
 * the care state the companion query already carries; there is NO decay-rate on the wire, so no
 * "next dip" is shown (MeScreen's rule: never invent a number). What IS real: `playReadyAt`, and the
 * warm low-care line (`lowCareCue`, never guilt copy).
 *
 * Groom borrows the Feed glyph: `HABITAT_DOCK_ICONS` has no groom/wash cel (the phone dock reaches
 * grooming through the Feed key's care tray, so it never needed one).
 */
export function Dashboard({
  companion, now = new Date(), disabled, onCooldown, playBlocked = false, wardrobeOn,
  onFeed, onPlay, onGroom, onDress, onEquip, onLooks, onAbilities,
  feedRef, playRef, groomRef, dressRef, equipRef, looksRef, abilitiesRef,
  looksCount, abilitiesOpen,
}: DashboardProps) {
  const { care } = companion;
  const cue = lowCareCue(care, companion.name);
  const cards: {
    key: 'hunger' | 'joy' | 'grooming'; label: string; value: number; action: string; icon: HabitatDockIconId;
    onClick: () => void; cooldown: boolean; blocked: boolean; note: string | null; ref?: ButtonRef;
  }[] = [
    { key: 'hunger', label: 'Hunger', value: care.hunger, action: 'Feed', icon: 'feed', onClick: onFeed, cooldown: false, blocked: false, note: null, ref: feedRef },
    {
      key: 'joy', label: 'Joy', value: care.joy, action: 'Play', icon: 'play', onClick: onPlay, cooldown: onCooldown, blocked: playBlocked,
      note: onCooldown && care.playReadyAt != null ? `Play again in ${formatEta(care.playReadyAt, now)}` : null, ref: playRef,
    },
    { key: 'grooming', label: 'Grooming', value: care.grooming, action: 'Groom', icon: 'feed', onClick: onGroom, cooldown: false, blocked: false, note: null, ref: groomRef },
  ];
  return (
    <div className="dash">
      <h2 className="dash-title">Dashboard <small>{companion.name}</small></h2>
      {cards.map((c) => {
        const noteId = `dash-note-${c.key}`;
        return (
          <div key={c.key} className="dash-card">
            <div className="dash-card-head"><span>{c.label}</span><small>{c.value} / 100</small></div>
            <div role="meter" aria-label={c.label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={c.value} className="dash-meter"><i style={{ width: `${c.value}%` }} /></div>
            {/* Both facts when both are true (a low Joy on cooldown): the warm cue is the invitation,
                the cooldown is the §11.10 reason the button is disabled — neither may hide the other. */}
            <p id={noteId} className="dash-note">{[cue && cue.key === c.key ? cue.note : null, c.note].filter(Boolean).join(' ')}</p>
            {/* SCREENS.md /habitat "play-cooldown": the verb stays the label, the reason is the note
                above (linked by `aria-describedby` so it is announced with the button);
                `aria-disabled` keeps the button focusable and announced (§11.10). */}
            <button
              ref={c.ref}
              type="button"
              className="btn-primary dash-action"
              disabled={disabled || c.blocked}
              aria-disabled={c.cooldown || undefined}
              aria-describedby={c.cooldown ? noteId : undefined}
              onClick={c.cooldown ? undefined : c.onClick}
            >
              <span className="habitat-dock-glyph" aria-hidden="true"><HabitatDockIcon id={c.icon} /></span>{c.action}
            </button>
          </div>
        );
      })}
      <div className="dash-acts">
        {wardrobeOn && (
          <button ref={dressRef} type="button" className="dash-act" disabled={disabled} onClick={onDress}><HabitatDockIcon id="dress" /><span>Dress<small>Wardrobe</small></span></button>
        )}
        <button ref={equipRef} type="button" className="dash-act" disabled={disabled} onClick={onEquip}><HabitatDockIcon id="equip" /><span>Equip<small>Gear</small></span></button>
        {wardrobeOn && (
          <button ref={looksRef} type="button" className="dash-act" disabled={disabled} onClick={onLooks}><HabitatDockIcon id="looks" /><span>Looks{looksCount != null && <small>{looksCount} saved</small>}</span></button>
        )}
        <button ref={abilitiesRef} type="button" className="dash-act" disabled={disabled} onClick={onAbilities}><HabitatDockIcon id="abilities" /><span>Abilities{abilitiesOpen && <small>{abilitiesOpen}</small>}</span></button>
      </div>
    </div>
  );
}
